import { API_URL } from '../../config';
import { alpha } from '@mui/material/styles';
import PageHeader from '../../components/PageHeader';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircle,
  CloudUpload as UploadIcon,
  ExpandLess,
  ExpandMore,
  GetApp as DownloadIcon,
  Link as LinkIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Warning,
} from '@mui/icons-material';
import { Device } from '../../types';
import { deviceAPI, vehicleAPI } from '../../services/api';
import AddDeviceDialog from '../../components/AddDeviceDialog';
import BulkImportDialog from '../../components/BulkImportDialog';

// ─── Types ────────────────────────────────────────────────────────────────────

interface DeviceWithTelemetry extends Device {
  lastSpeed?: number;
  latitude?: number;
  longitude?: number;
  address?: string;
  engineStatus?: boolean;
  lastUpdate?: string;
  heading?: number;
  altitude?: number;
  satellites?: number;
  batteryVoltage?: string | null;
  externalVoltage?: string | null;
  totalDistance?: number;
  gsmSignal?: number;
  idleTime?: number;
}

type SortField = 'plateNumber' | 'status' | 'lastSpeed' | 'totalDistance' | 'lastUpdate';
type SortDir = 'asc' | 'desc';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getCardinalDirection = (degrees: number): string => {
  const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  return directions[Math.round(degrees / 45) % 8];
};

const renderSignalBars = (signal: number): React.ReactNode => {
  if (!signal || signal === 0) return <span style={{ color: '#ef4444', fontWeight: 700 }}>✗</span>;
  const bars = ['▂', '▄', '▆', '█'];
  const count = Math.min(Math.round((signal / 5) * 4), 4);
  return (
    <span style={{ letterSpacing: 1, fontSize: 12 }}>
      {bars.map((bar, i) => (
        <span key={i} style={{ color: i < count ? '#22c55e' : '#374151', fontWeight: 700 }}>
          {bar}
        </span>
      ))}
    </span>
  );
};

const getDeviceStatus = (device: DeviceWithTelemetry) => {
  if (device.status === 'inactive') return { status: 'inactive', label: 'Inactive', color: 'default' as const };
  if (device.status === 'online')  return { status: 'online',   label: 'Online',   color: 'success' as const };
  if (device.status === 'offline') return { status: 'offline',  label: 'Offline',  color: 'error'   as const };
  const last = device.lastUpdate ? (Date.now() - new Date(device.lastUpdate).getTime()) / 60000 : Infinity;
  if (last > 15) return { status: 'offline', label: 'Offline', color: 'error' as const };
  return { status: 'offline', label: 'Offline', color: 'error' as const };
};

const enrichDevicesWithTelemetry = async (devicesList: Device[]): Promise<DeviceWithTelemetry[]> => {
  if (devicesList.length === 0) return [];
  const imeis = devicesList.map(d => d.imei).join(',');
  let telemetryMap: Record<string, any> = {};
  try {
    const response = await fetch(
      `${API_URL}/telemetry/latest-bulk?imeis=${imeis}`,
      { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
    );
    if (response.ok) {
      const result = await response.json();
      telemetryMap = result.data || {};
    }
  } catch { /* silent — telemetry is best-effort */ }

  return devicesList.map(device => {
    const telemetry = telemetryMap[device.imei];
    if (!telemetry) {
      return {
        ...device,
        lastSpeed: 0,
        latitude: device.location?.coordinates?.[1],
        longitude: device.location?.coordinates?.[0],
        address: 'Unknown',
        engineStatus: false,
      };
    }
    const reported = telemetry.state?.reported || {};
    const isJTT808 = telemetry.manufacturer === 'JTT808' || telemetry.protocol === 'JTT808';
    const speed = isJTT808 ? (telemetry.speed_kmh ?? 0) : (reported.sp ?? 0);

    let latitude: number | undefined;
    let longitude: number | undefined;
    if (telemetry.parsed_location) {
      latitude  = telemetry.parsed_location.latitude;
      longitude = telemetry.parsed_location.longitude;
    } else if (reported.latlng) {
      const [lat, lng] = reported.latlng.split(',').map((c: string) => parseFloat(c.trim()));
      latitude  = lat;
      longitude = lng;
    } else if (telemetry.latitude && telemetry.longitude) {
      latitude  = telemetry.latitude;
      longitude = telemetry.longitude;
    }

    const batteryVoltage = reported['67'] != null
      ? (reported['67'] / 1000).toFixed(2)
      : typeof telemetry.battery_voltage_V === 'number'
        ? telemetry.battery_voltage_V.toFixed(2)
        : null;

    const externalVoltage = reported['66'] != null
      ? (reported['66'] / 1000).toFixed(2)
      : null;

    return {
      ...device,
      lastSpeed: Math.round(speed),
      latitude,
      longitude,
      address: latitude && longitude ? `${latitude.toFixed(4)}, ${longitude.toFixed(4)}` : 'Unknown',
      engineStatus: isJTT808 ? telemetry.acc_on === true : reported['239'] === 1,
      batteryVoltage,
      externalVoltage,
      totalDistance: reported['16'] ? Math.round(reported['16'] / 1000) : (telemetry.total_mileage_km ?? 0),
      gsmSignal: reported['21'] ?? telemetry.wireless_signal_strength ?? 0,
      satellites: reported.sat ?? telemetry.gnss_satellites ?? 0,
      heading: reported.ang ?? telemetry.direction ?? 0,
      altitude: reported.alt ?? telemetry.altitude ?? 0,
      lastUpdate: telemetry.processed_at,
    };
  });
};

// ─── Quick Vehicle Dialog (outside main component to avoid re-render) ─────────

interface QuickVehicleDialogProps {
  open: boolean;
  onClose: () => void;
  device: DeviceWithTelemetry | null;
  onCreated: () => void;
}

const QuickVehicleDialog: React.FC<QuickVehicleDialogProps> = ({ open, onClose, device, onCreated }) => {
  const [vehicleData, setVehicleData] = useState({
    plateNumber: '',
    category: 'general' as 'executive' | 'staff_bus' | 'technical' | 'general',
    make: '',
    model: '',
    year: new Date().getFullYear(),
    color: '',
    fuelType: 'petrol' as 'petrol' | 'diesel' | 'electric' | 'hybrid',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (device) {
      setVehicleData(v => ({
        ...v,
        plateNumber: device.plateNumber || '',
        make: device.make || '',
        model: device.model || '',
        year: parseInt(device.modelYear || String(new Date().getFullYear())),
      }));
      setError('');
    }
  }, [device]);

  const handleCreate = async () => {
    if (!device?.vin) { setError('Device does not have a VIN'); return; }
    if (!vehicleData.plateNumber) { setError('Plate number is required'); return; }
    setLoading(true);
    setError('');
    try {
      await vehicleAPI.quickAddFromDevice({
        deviceId: device._id!,
        vin: device.vin,
        ...vehicleData,
      });
      onCreated();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create vehicle');
    } finally {
      setLoading(false);
    }
  };

  if (!device) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Vehicle from Device</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2"><strong>Device:</strong> {device.imei}</Typography>
            <Typography variant="body2"><strong>VIN:</strong> {device.vin}</Typography>
            <Typography variant="body2">Creating a vehicle will automatically bind this device.</Typography>
          </Alert>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth required label="Plate Number"
                value={vehicleData.plateNumber}
                onChange={e => setVehicleData(v => ({ ...v, plateNumber: e.target.value.toUpperCase() }))} />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select value={vehicleData.category} label="Category"
                  onChange={e => setVehicleData(v => ({ ...v, category: e.target.value as any }))}>
                  <MenuItem value="executive">Executive Pool</MenuItem>
                  <MenuItem value="staff_bus">Staff Bus</MenuItem>
                  <MenuItem value="technical">Technical Units</MenuItem>
                  <MenuItem value="general">General Pool</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth required label="Make" value={vehicleData.make}
                onChange={e => setVehicleData(v => ({ ...v, make: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth required label="Model" value={vehicleData.model}
                onChange={e => setVehicleData(v => ({ ...v, model: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth required type="number" label="Year" value={vehicleData.year}
                onChange={e => setVehicleData(v => ({ ...v, year: parseInt(e.target.value) }))}
                inputProps={{ min: 1990, max: new Date().getFullYear() + 1 }} />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth required>
                <InputLabel>Fuel Type</InputLabel>
                <Select value={vehicleData.fuelType} label="Fuel Type"
                  onChange={e => setVehicleData(v => ({ ...v, fuelType: e.target.value as any }))}>
                  <MenuItem value="petrol">Petrol</MenuItem>
                  <MenuItem value="diesel">Diesel</MenuItem>
                  <MenuItem value="electric">Electric</MenuItem>
                  <MenuItem value="hybrid">Hybrid</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Color (optional)" value={vehicleData.color}
                onChange={e => setVehicleData(v => ({ ...v, color: e.target.value }))} />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" onClick={handleCreate}
          disabled={loading || !vehicleData.plateNumber}
          startIcon={loading ? <CircularProgress size={16} /> : undefined}>
          {loading ? 'Creating…' : 'Create & Bind'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Expandable Row ───────────────────────────────────────────────────────────

interface ExpandedRowProps {
  device: DeviceWithTelemetry;
  onQuickAdd: (d: DeviceWithTelemetry) => void;
}

const ExpandedRow: React.FC<ExpandedRowProps> = ({ device, onQuickAdd }) => {
  const [address, setAddress] = useState<string>('');
  const [loadingAddr, setLoadingAddr] = useState(false);
  const s = getDeviceStatus(device);

  const loadAddress = async () => {
    if (!device.latitude || !device.longitude || address || loadingAddr) return;
    setLoadingAddr(true);
    try {
      const res = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${device.longitude},${device.latitude}.json?access_token=${process.env.REACT_APP_MAPBOX_TOKEN || 'pk.eyJ1Ijoic29waGllMDEiLCJhIjoiY21jbmZtaHBkMDNhMjJuczMxd3dmeWVobCJ9.7W1F9G-KWZRqsiNPEle4sA'}`
      );
      const data = await res.json();
      setAddress(data.features?.[0]?.place_name || 'Unknown location');
    } catch {
      setAddress(`${device.latitude?.toFixed(4)}, ${device.longitude?.toFixed(4)}`);
    } finally {
      setLoadingAddr(false);
    }
  };

  return (
    <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
      <Grid container spacing={2}>
        {/* Signal & Satellites */}
        <Grid item xs={6} sm={3}>
          <Typography variant="caption" color="text.secondary" display="block">Signal</Typography>
          <Box display="flex" alignItems="center" gap={0.5}>
            {renderSignalBars(device.gsmSignal || 0)}
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              {device.satellites || 0} sats
            </Typography>
          </Box>
        </Grid>

        {/* Battery */}
        <Grid item xs={6} sm={3}>
          <Typography variant="caption" color="text.secondary" display="block">Battery</Typography>
          <Typography variant="body2">
            {device.batteryVoltage && parseFloat(device.batteryVoltage) > 0
              ? `${device.batteryVoltage}V`
              : <span style={{ color: '#6b7280' }}>N/A</span>}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Ext: {device.externalVoltage ? `${device.externalVoltage}V` : '—'}
          </Typography>
        </Grid>

        {/* Odometer */}
        <Grid item xs={6} sm={3}>
          <Typography variant="caption" color="text.secondary" display="block">Odometer</Typography>
          <Typography variant="body2">
            {device.totalDistance ? `${device.totalDistance.toLocaleString()} km` : '—'}
          </Typography>
        </Grid>

        {/* Altitude */}
        <Grid item xs={6} sm={3}>
          <Typography variant="caption" color="text.secondary" display="block">Altitude</Typography>
          <Typography variant="body2">
            {device.altitude != null && device.altitude > 0 ? `${device.altitude}m` : '—'}
          </Typography>
        </Grid>

        {/* Coordinates */}
        {device.latitude && device.longitude && (
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" display="block">Coordinates</Typography>
            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
              {device.latitude.toFixed(6)}, {device.longitude.toFixed(6)}
            </Typography>
          </Grid>
        )}

        {/* Address (lazy loaded) */}
        {device.latitude && device.longitude && (
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" display="block">Address</Typography>
            {address ? (
              <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {address}
              </Typography>
            ) : (
              <Button size="small" variant="text" onClick={loadAddress} disabled={loadingAddr} sx={{ p: 0, minWidth: 0, textTransform: 'none', fontSize: '0.8rem' }}>
                {loadingAddr ? <><CircularProgress size={10} sx={{ mr: 0.5 }} />Loading…</> : 'Load address'}
              </Button>
            )}
          </Grid>
        )}

        {/* Last Update */}
        {device.lastUpdate && (
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" display="block">Last Update</Typography>
            <Typography variant="body2">
              {new Date(device.lastUpdate).toLocaleString()}
            </Typography>
          </Grid>
        )}

        {/* IMEI */}
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" color="text.secondary" display="block">IMEI</Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{device.imei}</Typography>
        </Grid>

        {/* VIN & actions */}
        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            {device.vin ? (
              <>
                <Chip size="small" icon={<CheckCircle />} label={`VIN: ${device.vin}`} color="success" sx={{ fontSize: '0.7rem' }} />
                {!device.isBoundToVehicle && (
                  <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => onQuickAdd(device)}>
                    Create Vehicle
                  </Button>
                )}
                {device.isBoundToVehicle && (
                  <Chip size="small" icon={<LinkIcon />} label="Vehicle Bound" color="success" sx={{ fontSize: '0.7rem' }} />
                )}
              </>
            ) : (
              <Chip size="small" icon={<Warning />} label="No VIN" color="warning" sx={{ fontSize: '0.7rem' }} />
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

// ─── Summary stat card ────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: number | string;
  color?: string;
  onClick?: () => void;
  active?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, color, onClick, active }) => (
  <Box
    onClick={onClick}
    sx={{
      px: 2.5,
      py: 1.5,
      borderRadius: 2,
      border: '1px solid',
      borderColor: active ? (color || 'primary.main') : 'divider',
      bgcolor: theme => active ? alpha(color || theme.palette.primary.main, 0.08) : theme.palette.background.paper,
      cursor: onClick ? 'pointer' : 'default',
      transition: 'all 0.15s ease',
      '&:hover': onClick ? { borderColor: color || 'primary.main', bgcolor: theme => alpha(color || theme.palette.primary.main, 0.04) } : {},
      minWidth: 90,
      textAlign: 'center',
    }}
  >
    <Typography variant="h5" fontWeight={700} sx={{ color: active ? (color || 'primary.main') : 'text.primary', lineHeight: 1.2 }}>
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
      {label}
    </Typography>
  </Box>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const DevicesPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // ── Data state
  const [devices, setDevices]               = useState<DeviceWithTelemetry[]>([]);
  const [totalCount, setTotalCount]         = useState(0);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState('');

  // ── Pagination & sorting
  const [page, setPage]                     = useState(0);   // 0-indexed for MUI
  const [rowsPerPage, setRowsPerPage]       = useState(50);
  const [sortField, setSortField]           = useState<SortField>('plateNumber');
  const [sortDir, setSortDir]               = useState<SortDir>('asc');

  // ── Filters (server-side)
  const [searchInput, setSearchInput]       = useState('');
  const [searchQuery, setSearchQuery]       = useState('');   // debounced
  const [statusFilter, setStatusFilter]     = useState('');
  const [typeFilter, setTypeFilter]         = useState('');

  // ── UI state
  const [expandedRow, setExpandedRow]       = useState<string | null>(null);
  const [anchorEl, setAnchorEl]             = useState<null | HTMLElement>(null);
  const [openAdd, setOpenAdd]               = useState(false);
  const [openBulkImport, setOpenBulkImport] = useState(false);
  const [quickAddDevice, setQuickAddDevice] = useState<DeviceWithTelemetry | null>(null);

  // ── Debounce search
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setSearchQuery(value);
      setPage(0);
    }, 400);
  };

  // ── Load (server-side everything)
  const loadDevices = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await deviceAPI.getAllDevices({
        page: page + 1,
        limit: rowsPerPage,
        search: searchQuery || undefined,
        status: statusFilter || undefined,
        deviceType: typeFilter || undefined,
        sortField,
        sortDir,
      });

      // getAllDevices now returns { data: Device[], pagination: { total, ... } }
      const list: Device[] = result.data ?? [];
      const total: number  = result.pagination?.total ?? list.length;

      const enriched = await enrichDevicesWithTelemetry(list);
      setDevices(enriched);
      setTotalCount(total);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load devices');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchQuery, statusFilter, typeFilter, sortField, sortDir]);

  useEffect(() => { loadDevices(); }, [loadDevices]);

  // ── Sort handler
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setPage(0);
  };

  // ── Status counts (from current page — we also show server total)
  const counts = useMemo(() => {
    const online   = devices.filter(d => getDeviceStatus(d).status === 'online').length;
    const offline  = devices.filter(d => getDeviceStatus(d).status === 'offline').length;
    const inactive = devices.filter(d => getDeviceStatus(d).status === 'inactive').length;
    return { online, offline, inactive };
  }, [devices]);

  // ── Download template
  const downloadTemplate = async () => {
    try {
      const res = await fetch(
        `${API_URL}/devices/bulk-import/template`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (res.ok) {
        const blob = await res.blob();
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href = url; a.download = 'device_import_template.csv';
        document.body.appendChild(a); a.click();
        document.body.removeChild(a); URL.revokeObjectURL(url);
      }
    } catch { /* silent */ }
    setAnchorEl(null);
  };

  const activeFilters = [searchQuery, statusFilter, typeFilter].filter(Boolean).length;

  // ──────────────────────────────────────────────────────────────────────────
  // RENDER
  // ──────────────────────────────────────────────────────────────────────────
  return (
    <Box>
      <PageHeader title="Vehicles & Assets" description={<>Manage your vehicles, connected devices, and asset details · {totalCount.toLocaleString()} devices registered{activeFilters > 0 && ` · ${activeFilters} filters active`}</>} icon="vehicles" actions={<>
<Stack direction="row" spacing={1}>
              <Tooltip title="Refresh">
                <IconButton onClick={loadDevices} size="small" disabled={loading}>
                  {loading ? <CircularProgress size={18} /> : <RefreshIcon />}
                </IconButton>
              </Tooltip>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenAdd(true)} size="small">
                Add Device
              </Button>
              <IconButton size="small" onClick={e => setAnchorEl(e.currentTarget)}>
                <MoreVertIcon />
              </IconButton>
              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                <MenuItem onClick={() => { setOpenBulkImport(true); setAnchorEl(null); }}>
                  <ListItemIcon><UploadIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Bulk Import</ListItemText>
                </MenuItem>
                <MenuItem onClick={downloadTemplate}>
                  <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Download Template</ListItemText>
                </MenuItem>
              </Menu>
            </Stack>
</>} />
      <Card>
        <CardContent>

          {/* ── Header ── */}


          {/* ── Stat Cards ── */}
          <Stack direction="row" spacing={1.5} mb={3} flexWrap="wrap" useFlexGap>
            <StatCard label="All" value={totalCount} onClick={() => { setStatusFilter(''); setPage(0); }} active={!statusFilter} />
            <StatCard label="Online"   value={counts.online}   color="#22c55e" onClick={() => { setStatusFilter('online');   setPage(0); }} active={statusFilter === 'online'} />
            <StatCard label="Offline"  value={counts.offline}  color="#ef4444" onClick={() => { setStatusFilter('offline');  setPage(0); }} active={statusFilter === 'offline'} />
            <StatCard label="Inactive" value={counts.inactive} color="#9ca3af" onClick={() => { setStatusFilter('inactive'); setPage(0); }} active={statusFilter === 'inactive'} />
          </Stack>

          {/* ── Filters ── */}
          <Grid container spacing={1.5} alignItems="center" mb={2}>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth size="small"
                placeholder="Search IMEI, plate, make, model, VIN…"
                value={searchInput}
                onChange={e => handleSearchChange(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: searchInput ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => { setSearchInput(''); handleSearchChange(''); }}>
                        ✕
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={statusFilter} label="Status" onChange={e => { setStatusFilter(e.target.value); setPage(0); }}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="online">Online</MenuItem>
                  <MenuItem value="offline">Offline</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Device Type</InputLabel>
                <Select value={typeFilter} label="Device Type" onChange={e => { setTypeFilter(e.target.value); setPage(0); }}>
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="My Car Pro">My Car Pro</MenuItem>
                  <MenuItem value="My Car Lite">My Car Lite</MenuItem>
                  <MenuItem value="Teltonika">Teltonika</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {(statusFilter || typeFilter || searchQuery) && (
              <Grid item xs={12} md={2}>
                <Button
                  size="small" variant="text" color="inherit"
                  onClick={() => { setStatusFilter(''); setTypeFilter(''); setSearchInput(''); setSearchQuery(''); setPage(0); }}
                  sx={{ textTransform: 'none', color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                >
                  Clear filters
                </Button>
              </Grid>
            )}
          </Grid>

          {/* ── Error ── */}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {/* ── Loading skeleton ── */}
          {loading && (
            <Box display="flex" justifyContent="center" alignItems="center" py={8} gap={2}>
              <CircularProgress size={24} />
              <Typography color="text.secondary">Loading devices…</Typography>
            </Box>
          )}

          {/* ── Empty state ── */}
          {!loading && !error && devices.length === 0 && (
            <Box display="flex" flexDirection="column" alignItems="center" py={8} gap={2}>
              <Typography variant="h6" color="text.secondary">No devices found</Typography>
              <Typography variant="body2" color="text.secondary">
                {activeFilters > 0 ? 'Try adjusting or clearing your filters.' : 'Add your first device to get started.'}
              </Typography>
              {activeFilters > 0 && (
                <Button variant="outlined" size="small" onClick={() => { setStatusFilter(''); setTypeFilter(''); setSearchInput(''); setSearchQuery(''); setPage(0); }}>
                  Clear Filters
                </Button>
              )}
            </Box>
          )}

          {/* ── Mobile Cards ── */}
          {!loading && !error && devices.length > 0 && isMobile && (
            <Box>
              {devices.map(d => {
                const s = getDeviceStatus(d);
                const isExpanded = expandedRow === (d._id || d.imei);
                return (
                  <Card key={d._id || d.imei} variant="outlined" sx={{ mb: 1.5 }}>
                    <CardContent sx={{ pb: '12px !important' }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box flex={1} minWidth={0}>
                          <Typography variant="subtitle2" fontWeight={700} noWrap>
                            {(typeof d.vehicleId === 'object' && (d.vehicleId as any)?.plateNumber) || d.plateNumber || d.make || 'Unbound Device'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap display="block">
                            {d.imei}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Chip size="small" color={s.color} label={s.label} />
                          <IconButton size="small" onClick={() => setExpandedRow(isExpanded ? null : (d._id || d.imei))}>
                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </Stack>
                      </Box>

                      <Stack direction="row" useFlexGap flexWrap="wrap" spacing={2} mt={1}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Speed</Typography>
                          <Typography variant="body2" color={d.lastSpeed && d.lastSpeed > 0 ? 'success.main' : 'text.secondary'} fontWeight={d.lastSpeed ? 600 : 400}>
                            {d.lastSpeed || 0} km/h
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Engine</Typography>
                          <Chip size="small" label={d.engineStatus && s.status !== 'offline' ? 'ON' : 'OFF'}
                            color={d.engineStatus && s.status !== 'offline' ? 'success' : 'default'}
                            sx={{ height: 20, fontSize: '0.7rem' }} />
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Type</Typography>
                          <Typography variant="body2">{d.deviceType || '—'}</Typography>
                        </Box>
                      </Stack>

                      {isExpanded && (
                        <Box mt={1.5}>
                          <ExpandedRow device={d} onQuickAdd={dev => setQuickAddDevice(dev)} />
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
              {/* Mobile pagination */}
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
                rowsPerPageOptions={[25, 50, 100]}
              />
            </Box>
          )}

          {/* ── Desktop Table ── */}
          {!loading && !error && devices.length > 0 && !isMobile && (
            <Box sx={{ overflowX: 'auto' }}>
              <Box sx={{ width: '100%', overflowX: 'auto' }}><Table size="small" sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, whiteSpace: 'nowrap', py: 1.5 } }}>
                    <TableCell sx={{ width: 48 }}>#</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'plateNumber'} direction={sortDir}
                        onClick={() => handleSort('plateNumber')}>
                        Vehicle / Device
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'status'} direction={sortDir}
                        onClick={() => handleSort('status')}>
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'lastSpeed'} direction={sortDir}
                        onClick={() => handleSort('lastSpeed')}>
                        Speed
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Engine</TableCell>
                    <TableCell>Signal / Sats</TableCell>
                    <TableCell>Battery</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'totalDistance'} direction={sortDir}
                        onClick={() => handleSort('totalDistance')}>
                        Odometer
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ width: 40 }}>
                      {/* Expand column */}
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {devices.map((d, idx) => {
                    const s         = getDeviceStatus(d);
                    const isExp     = expandedRow === (d._id || d.imei);
                    const rowNum    = page * rowsPerPage + idx + 1;
                    const vehicleData = typeof d.vehicleId === 'object' && d.vehicleId ? d.vehicleId as any : null;
                    const displayName = vehicleData?.plateNumber || d.plateNumber || d.make || 'Unbound Device';
                    const subName   = [vehicleData?.make || d.make, vehicleData?.model || d.model, vehicleData?.year || d.modelYear].filter(Boolean).join(' ');

                    return (
                      <React.Fragment key={d._id || d.imei}>
                        <TableRow
                          hover
                          sx={{
                            cursor: 'pointer',
                            bgcolor: isExp ? 'action.selected' : undefined,
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                          onClick={() => setExpandedRow(isExp ? null : (d._id || d.imei))}
                        >
                          {/* # */}
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">{rowNum}</Typography>
                          </TableCell>

                          {/* Vehicle / Device */}
                          <TableCell>
                            <Typography variant="body2" fontWeight={600} noWrap>{displayName}</Typography>
                            {subName && (
                              <Typography variant="caption" color="text.secondary" noWrap display="block">
                                {subName}
                              </Typography>
                            )}
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.68rem', color: 'text.disabled' }}>
                              {d.imei}
                            </Typography>
                          </TableCell>

                          {/* Type */}
                          <TableCell>
                            <Chip size="small" label={d.deviceType || '?'}
                              color={d.deviceType === 'My Car Pro' ? 'primary' : 'default'}
                              sx={{ fontSize: '0.7rem' }} />
                          </TableCell>

                          {/* Status */}
                          <TableCell>
                            <Chip size="small" color={s.color} label={s.label} />
                          </TableCell>

                          {/* Speed */}
                          <TableCell>
                            <Typography
                              variant="body2"
                              color={d.lastSpeed && d.lastSpeed > 0 && s.status !== 'offline' ? 'success.main' : 'text.secondary'}
                              fontWeight={d.lastSpeed && d.lastSpeed > 0 ? 600 : 400}
                            >
                              {s.status === 'offline' ? 0 : (d.lastSpeed || 0)} km/h
                            </Typography>
                            {d.heading !== undefined && d.heading > 0 && (
                              <Typography variant="caption" color="text.disabled">
                                {d.heading}° {getCardinalDirection(d.heading)}
                              </Typography>
                            )}
                          </TableCell>

                          {/* Engine */}
                          <TableCell>
                            <Chip
                              size="small"
                              label={d.engineStatus && s.status !== 'offline' ? 'ON' : 'OFF'}
                              color={d.engineStatus && s.status !== 'offline' ? 'success' : 'default'}
                              sx={{ minWidth: 44 }}
                            />
                          </TableCell>

                          {/* Signal */}
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              {renderSignalBars(d.gsmSignal || 0)}
                              <Typography variant="caption" color="text.secondary">
                                {d.satellites || 0}
                              </Typography>
                            </Box>
                          </TableCell>

                          {/* Battery */}
                          <TableCell>
                            <Typography variant="body2">
                              {d.batteryVoltage && parseFloat(d.batteryVoltage) > 0
                                ? `${d.batteryVoltage}V`
                                : <span style={{ color: '#9ca3af' }}>N/A</span>}
                            </Typography>
                          </TableCell>

                          {/* Odometer */}
                          <TableCell>
                            <Typography variant="body2">
                              {d.totalDistance ? `${d.totalDistance.toLocaleString()} km` : '—'}
                            </Typography>
                          </TableCell>

                          {/* Expand toggle */}
                          <TableCell onClick={e => e.stopPropagation()}>
                            <IconButton size="small" onClick={() => setExpandedRow(isExp ? null : (d._id || d.imei))}>
                              {isExp ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                            </IconButton>
                          </TableCell>
                        </TableRow>

                        {/* Expanded detail row */}
                        {isExp && (
                          <TableRow>
                            <TableCell colSpan={10} sx={{ py: 0, px: 2, pb: 1.5 }}>
                              <ExpandedRow device={d} onQuickAdd={dev => setQuickAddDevice(dev)} />
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>

                <TableFooter>
                  <TableRow>
                    <TablePagination
                      colSpan={10}
                      count={totalCount}
                      page={page}
                      onPageChange={(_, p) => setPage(p)}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
                      rowsPerPageOptions={[25, 50, 100, 200]}
                      labelRowsPerPage="Rows:"
                      labelDisplayedRows={({ from, to, count }) =>
                        `${from}–${to} of ${count.toLocaleString()}`
                      }
                    />
                  </TableRow>
                </TableFooter>
              </Table></Box>
            </Box>
          )}

        </CardContent>
      </Card>

      {/* ── Dialogs ── */}
      <AddDeviceDialog open={openAdd} onClose={() => setOpenAdd(false)} onAdded={loadDevices as any} />
      <BulkImportDialog open={openBulkImport} onClose={() => setOpenBulkImport(false)}
        onImported={(count: number) => { console.log(`${count} imported`); loadDevices(); }} />
      <QuickVehicleDialog
        open={Boolean(quickAddDevice)}
        onClose={() => setQuickAddDevice(null)}
        device={quickAddDevice}
        onCreated={loadDevices}
      />
    </Box>
  );
};

export default DevicesPage;
